import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
} from '@angular/core';
import { AuthService } from '../../services/auth.service';

/**
 * Structural directive to conditionally render UI elements based on
 * the authenticated user's GARS permission set.
 *
 * Usage:
 *   <button *appHasPermission="'GEMINI_USAGE_EXPORT'">Export CSV</button>
 *   <div *appHasPermission="['GEMINI_USER_MANAGE', 'GEMINI_USAGE_ALL_READ']">...</div>
 *   <div *appHasPermission="['GEMINI_USER_MANAGE', 'GEMINI_USAGE_ALL_READ']; requireAll: true">...</div>
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: false, // Exported in SharedModule for template compatibility
})
export class HasPermissionDirective {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly vcr = inject(ViewContainerRef);
  private readonly authService = inject(AuthService);

  private requiredPermissions: string[] = [];
  private requireAll = false;
  private hasView = false;

  @Input()
  set appHasPermission(permission: string | string[]) {
    if (!permission) {
      this.requiredPermissions = [];
    } else if (Array.isArray(permission)) {
      this.requiredPermissions = permission;
    } else {
      this.requiredPermissions = [permission];
    }
    this.updateView();
  }

  @Input()
  set appHasPermissionRequireAll(requireAll: boolean) {
    this.requireAll = requireAll;
    this.updateView();
  }

  constructor() {
    // React to reactive changes in user permissions or role
    effect(() => {
      // Access reactive signals so effect tracks them
      this.authService.permissions();
      this.authService.userRole();
      this.updateView();
    });
  }

  private updateView(): void {
    const isPermitted = this.checkPermission();

    if (isPermitted && !this.hasView) {
      this.vcr.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!isPermitted && this.hasView) {
      this.vcr.clear();
      this.hasView = false;
    }
  }

  private checkPermission(): boolean {
    if (this.requiredPermissions.length === 0) {
      return true;
    }

    // Super Admin role has elevated access
    if (this.authService.isAdmin()) {
      return true;
    }

    const currentPermissions = this.authService.permissions();
    if (!currentPermissions || currentPermissions.length === 0) {
      return false; // Safe default: hide until permissions are known
    }

    if (this.requireAll) {
      return this.requiredPermissions.every((p) => currentPermissions.includes(p));
    }

    return this.requiredPermissions.some((p) => currentPermissions.includes(p));
  }
}
